/**
 * Alumni Directory Page
 * Searchable grid of alumni profiles with self-edit capability
 * Accessible at /app/alumni
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/context/PermissionsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Drawer } from '@/components/campus/Drawer';
import { toast } from 'sonner';
import { PageHeader, EmptyState, BadgePill } from '@/components/campus';
import {
  Users,
  GraduationCap,
  Building2,
  MapPin,
  Linkedin,
  Search,
  Filter,
  Loader2,
  Edit,
  X,
  ExternalLink,
  Mail,
  Calendar,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate graduation year options (current year back 50 years)
const currentYear = new Date().getFullYear();
const graduationYears = Array.from({ length: 50 }, (_, i) => currentYear - i);

// ==================== ALUMNI CARD ====================
const AlumniCard = ({ alumni, isCurrentUser, onViewProfile, onEdit }) => (
  <div
    className={cn(
      "p-5 bg-graphite/50 rounded-xl border transition-all cursor-pointer hover:border-electric-ice-blue/30",
      isCurrentUser ? "border-electric-ice-blue/40 ring-1 ring-electric-ice-blue/20" : "border-white/[0.06]"
    )}
    onClick={() => onViewProfile(alumni)}
    data-testid={`alumni-card-${alumni.user_id}`}
  >
    {/* Header with Avatar and Name */}
    <div className="flex items-start gap-4">
      {alumni.avatar_url ? (
        <img
          src={alumni.avatar_url}
          alt={alumni.display_name}
          className="w-16 h-16 rounded-full object-cover border-2 border-white/[0.1]"
        />
      ) : (
        <div className="w-16 h-16 rounded-full bg-electric-ice-blue/20 flex items-center justify-center">
          <GraduationCap className="w-7 h-7 text-electric-ice-blue" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-cloud-white truncate">{alumni.display_name}</h3>
          {isCurrentUser && (
            <BadgePill variant="blue" size="sm">You</BadgePill>
          )}
        </div>
        
        {alumni.graduation_year && (
          <p className="text-sm text-electric-ice-blue mt-0.5">Class of {alumni.graduation_year}</p>
        )}
      </div>

      {isCurrentUser && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(alumni);
          }}
          className="text-soft-silver hover:text-cloud-white"
          data-testid="edit-profile-btn"
        >
          <Edit className="w-4 h-4" />
        </Button>
      )}
    </div>

    {/* Info */}
    <div className="mt-4 space-y-2">
      {alumni.alumni_employer && (
        <div className="flex items-center gap-2 text-sm text-soft-silver">
          <Building2 className="w-4 h-4 text-muted-text" />
          <span className="truncate">{alumni.alumni_employer}</span>
        </div>
      )}
      {alumni.alumni_city && (
        <div className="flex items-center gap-2 text-sm text-soft-silver">
          <MapPin className="w-4 h-4 text-muted-text" />
          <span className="truncate">{alumni.alumni_city}</span>
        </div>
      )}
    </div>

    {/* LinkedIn Button */}
    {alumni.linkedin_url && (
      <Button
        variant="outline"
        size="sm"
        className="mt-4 w-full border-[#0077B5]/30 text-[#0077B5] hover:bg-[#0077B5]/10"
        onClick={(e) => {
          e.stopPropagation();
          window.open(alumni.linkedin_url, '_blank');
        }}
      >
        <Linkedin className="w-4 h-4 mr-2" />
        Connect on LinkedIn
      </Button>
    )}
  </div>
);

// ==================== PROFILE DRAWER ====================
const ProfileDrawer = ({ alumni, isOpen, onClose, isCurrentUser, onEdit }) => {
  if (!alumni) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Alumni Profile" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          {alumni.avatar_url ? (
            <img
              src={alumni.avatar_url}
              alt={alumni.display_name}
              className="w-20 h-20 rounded-full object-cover border-2 border-white/[0.1]"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-electric-ice-blue/20 flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-electric-ice-blue" />
            </div>
          )}

          <div>
            <h2 className="text-xl font-semibold text-cloud-white">{alumni.display_name}</h2>
            {alumni.graduation_year && (
              <p className="text-electric-ice-blue">Class of {alumni.graduation_year}</p>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="space-y-4 p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
          {alumni.email && (
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-text" />
              <a href={`mailto:${alumni.email}`} className="text-soft-silver hover:text-electric-ice-blue">
                {alumni.email}
              </a>
            </div>
          )}
          {alumni.alumni_employer && (
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-muted-text" />
              <span className="text-soft-silver">{alumni.alumni_employer}</span>
            </div>
          )}
          {alumni.alumni_city && (
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-muted-text" />
              <span className="text-soft-silver">{alumni.alumni_city}</span>
            </div>
          )}
          {alumni.graduation_year && (
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-muted-text" />
              <span className="text-soft-silver">Graduated {alumni.graduation_year}</span>
            </div>
          )}
        </div>

        {/* Bio */}
        {alumni.bio && (
          <div>
            <h4 className="text-sm font-medium text-soft-silver mb-2">About</h4>
            <p className="text-cloud-white text-sm leading-relaxed p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
              {alumni.bio}
            </p>
          </div>
        )}

        {/* LinkedIn */}
        {alumni.linkedin_url && (
          <Button
            className="w-full bg-[#0077B5] text-white hover:bg-[#0077B5]/90"
            onClick={() => window.open(alumni.linkedin_url, '_blank')}
          >
            <Linkedin className="w-4 h-4 mr-2" />
            View LinkedIn Profile
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        )}

        {/* Edit Button for Current User */}
        {isCurrentUser && (
          <Button
            variant="outline"
            className="w-full border-electric-ice-blue/30 text-electric-ice-blue"
            onClick={() => {
              onClose();
              onEdit(alumni);
            }}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit My Profile
          </Button>
        )}
      </div>
    </Drawer>
  );
};

// ==================== EDIT DRAWER ====================
const EditDrawer = ({ alumni, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    alumni_employer: '',
    alumni_city: '',
    graduation_year: '',
    linkedin_url: '',
    bio: '',
    opt_in_digest: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (alumni) {
      setFormData({
        alumni_employer: alumni.alumni_employer || '',
        alumni_city: alumni.alumni_city || '',
        graduation_year: alumni.graduation_year?.toString() || '',
        linkedin_url: alumni.linkedin_url || '',
        bio: alumni.bio || '',
        opt_in_digest: alumni.opt_in_digest ?? true,
      });
    }
  }, [alumni]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        ...formData,
        graduation_year: formData.graduation_year ? parseInt(formData.graduation_year) : null,
      });
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  if (!alumni) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Edit Alumni Profile" width="lg">
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-soft-silver">Current Employer</Label>
            <Input
              value={formData.alumni_employer}
              onChange={(e) => setFormData({ ...formData, alumni_employer: e.target.value })}
              placeholder="Company name"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-soft-silver">City</Label>
            <Input
              value={formData.alumni_city}
              onChange={(e) => setFormData({ ...formData, alumni_city: e.target.value })}
              placeholder="City, State"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-soft-silver">Graduation Year</Label>
            <Select
              value={formData.graduation_year}
              onValueChange={(v) => setFormData({ ...formData, graduation_year: v })}
            >
              <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent className="bg-graphite border-white/[0.08]">
                {graduationYears.map((year) => (
                  <SelectItem key={year} value={year.toString()} className="text-cloud-white">
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-soft-silver">LinkedIn URL</Label>
            <Input
              value={formData.linkedin_url}
              onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
              placeholder="https://linkedin.com/in/yourprofile"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-soft-silver">Bio</Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Tell us about yourself..."
              rows={4}
              className="bg-obsidian border-white/[0.08] text-cloud-white resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/[0.08]">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-electric-ice-blue text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Changes
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

// ==================== MAIN PAGE ====================
export const AlumniDirectory = () => {
  const { organization, session, user } = useAuth();
  const permissionsContext = usePermissions();

  // State
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    graduation_year: '',
    city: '',
    employer: '',
  });
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingAlumni, setEditingAlumni] = useState(null);

  // Check permission - safely handle if hasPermission is not available
  const canView = permissionsContext?.hasPermission 
    ? permissionsContext.hasPermission('alumni_directory') 
    : (permissionsContext?.role === 'owner' || permissionsContext?.role === 'admin');

  // Fetch alumni
  const fetchAlumni = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/alumni/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setAlumni(data.alumni || []);
      }
    } catch (error) {
      console.error('Failed to fetch alumni:', error);
      toast.error('Failed to load alumni directory');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchAlumni();
  }, [fetchAlumni]);

  // Filter alumni
  const filteredAlumni = alumni.filter((a) => {
    // Search filter
    const searchMatch =
      !searchQuery ||
      a.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.alumni_employer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.alumni_city?.toLowerCase().includes(searchQuery.toLowerCase());

    // Year filter
    const yearMatch = !filters.graduation_year || a.graduation_year?.toString() === filters.graduation_year;

    // City filter
    const cityMatch = !filters.city || a.alumni_city?.toLowerCase().includes(filters.city.toLowerCase());

    // Employer filter
    const employerMatch = !filters.employer || a.alumni_employer?.toLowerCase().includes(filters.employer.toLowerCase());

    return searchMatch && yearMatch && cityMatch && employerMatch;
  });

  // Get unique values for filters
  const uniqueYears = [...new Set(alumni.map((a) => a.graduation_year).filter(Boolean))].sort((a, b) => b - a);
  const uniqueCities = [...new Set(alumni.map((a) => a.alumni_city).filter(Boolean))];
  const uniqueEmployers = [...new Set(alumni.map((a) => a.alumni_employer).filter(Boolean))];

  // Handle view profile
  const handleViewProfile = (alumniMember) => {
    setSelectedAlumni(alumniMember);
    setIsProfileOpen(true);
  };

  // Handle edit
  const handleEdit = (alumniMember) => {
    setEditingAlumni(alumniMember);
    setIsEditOpen(true);
  };

  // Handle save
  const handleSave = async (data) => {
    try {
      const res = await fetch(
        `${API_URL}/api/alumni/${organization.id}/profile`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(data),
        }
      );

      if (res.ok) {
        toast.success('Profile updated');
        fetchAlumni();
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update profile');
      throw error;
    }
  };

  // Check if current user
  const isCurrentUser = (alumniMember) => alumniMember.user_id === user?.id;

  // Access denied
  if (!canView) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="lock"
          title="Access Restricted"
          description="You don't have permission to view the alumni directory."
        />
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading alumni directory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alumni-directory-page">
      <PageHeader
        title="Alumni Directory"
        subtitle={`${alumni.length} alumni in your network`}
        icon={GraduationCap}
      />

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
          <Input
            placeholder="Search by name, employer, or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-graphite border-white/[0.08] text-cloud-white"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Select
            value={filters.graduation_year}
            onValueChange={(v) => setFilters({ ...filters, graduation_year: v === 'all' ? '' : v })}
          >
            <SelectTrigger className="w-32 bg-graphite border-white/[0.08] text-cloud-white">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent className="bg-graphite border-white/[0.08]">
              <SelectItem value="all" className="text-muted-text">All Years</SelectItem>
              {uniqueYears.map((year) => (
                <SelectItem key={year} value={year.toString()} className="text-cloud-white">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {filters.graduation_year || filters.city || filters.employer ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ graduation_year: '', city: '', employer: '' })}
              className="text-soft-silver"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          ) : null}
        </div>
      </div>

      {/* Results Count */}
      <p className="text-sm text-muted-text">
        Showing {filteredAlumni.length} of {alumni.length} alumni
      </p>

      {/* Alumni Grid */}
      {alumni.length === 0 ? (
        <EmptyState
          icon="users"
          title="No Alumni Yet"
          description="Alumni will appear here when members transition to alumni status."
        />
      ) : filteredAlumni.length === 0 ? (
        <EmptyState
          icon="search"
          title="No Results"
          description="No alumni match your search criteria. Try adjusting your filters."
          actionLabel="Clear Filters"
          onAction={() => {
            setSearchQuery('');
            setFilters({ graduation_year: '', city: '', employer: '' });
          }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAlumni.map((alumniMember) => (
            <AlumniCard
              key={alumniMember.user_id}
              alumni={alumniMember}
              isCurrentUser={isCurrentUser(alumniMember)}
              onViewProfile={handleViewProfile}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      {/* Profile Drawer */}
      <ProfileDrawer
        alumni={selectedAlumni}
        isOpen={isProfileOpen}
        onClose={() => {
          setIsProfileOpen(false);
          setSelectedAlumni(null);
        }}
        isCurrentUser={selectedAlumni && isCurrentUser(selectedAlumni)}
        onEdit={handleEdit}
      />

      {/* Edit Drawer */}
      <EditDrawer
        alumni={editingAlumni}
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditingAlumni(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
};

export default AlumniDirectory;
